import { Info, Building2, Mail, CreditCard, CheckCircle2 } from 'lucide-react';
import { useUser } from '@clerk/clerk-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useOrganizationList } from '@clerk/clerk-react';
import { trpc } from '@/lib/api/trpc';
import { toast } from 'sonner';
import { getAcquisitionMetadataForOrganization } from '@/hooks/use-acquisition-tracking';
import { getOnboardingState } from '@/lib/onboarding';
import { currentPartnerBrand } from '@/config/current-partner';
import {
  captureAndPersistPartnerFromUrl,
  clearCapturedPartner,
  resolveSignupPartner,
} from '@/lib/utils/captured-partner';

interface CreateAccountProps {
  onAccountCreated: (orgSlug: string) => void;
  onCancel?: () => void;
}

export const CreateAccount: React.FC<CreateAccountProps> = ({ onAccountCreated, onCancel }) => {
  const { t } = useTranslation('auth');
  const { user } = useUser();
  const [accountName, setAccountName] = useState<string>('');
  const [billingEmail, setBillingEmail] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const { createOrganization, setActive, userMemberships } = useOrganizationList({
    userMemberships: { infinite: false },
  });
  const [isLoading, setIsLoading] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  // Pre-fill the billing email from the signed-in user only for their FIRST
  // account. Someone who already belongs to an organization is a partner rep
  // creating a merchant's account or a merchant opening a second one — in both
  // cases their own address is not the billing address, and a pre-filled field
  // gets committed as-is to the Stripe customer (#TAG-2148).
  const membershipCount = userMemberships?.count;
  useEffect(() => {
    if (membershipCount === undefined || membershipCount > 0) return;
    if (user?.primaryEmailAddress?.emailAddress) {
      setBillingEmail(user.primaryEmailAddress.emailAddress);
    }
  }, [user, membershipCount]);


  const utils = trpc.useUtils();
  const provisionAccountMutation = trpc.account.provisionAccount.useMutation();
  const updateBillingEmailMutation = trpc.accountPlans.updateBillingEmail.useMutation();
  const createPaymentSessionMutation = trpc.billing.getManagementUrl.useMutation();
  const updateOrgAcquisitionMutation = trpc.organizations.updateAcquisitionMetadata.useMutation();

  // Capture `?partner=<slug>` on mount so it survives the user
  // landing here directly (e.g. from a partner email) without
  // routing through the sign-up page first. The capture utility
  // is idempotent and persists to cookie + localStorage; reads
  // happen at submit time. See lib/utils/captured-partner.ts for
  // the full attribution contract.
  useEffect(() => {
    captureAndPersistPartnerFromUrl();
  }, []);

  const handleAccountNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setAccountName(value);
    if (value.length > 0 && value.length < 3) {
      setError(t('account.errors.name_too_short'));
    } else {
      setError(null);
    }
  };

  const handleBillingEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setBillingEmail(value);

    if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError(null);
    }
  };

  // Get error message from Clerk error code
  const getErrorMessage = (err: any): string => {
    const clerkError = err?.errors?.[0];
    if (clerkError?.code) {
      const errorKey = `account.errors.${clerkError.code}`;
      const translatedError = t(errorKey);
      if (translatedError !== errorKey) {
        return translatedError;
      }
    }

    return clerkError?.long_message || clerkError?.message || t('account.errors.generic');
  };

  const handleCreateAccount = async () => {
    // Validate inputs
    if (accountName.length < 3) {
      setError(t('account.errors.name_too_short'));
      return;
    }

    if (!billingEmail) {
      setEmailError('Billing email is required');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(billingEmail)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    if (!createOrganization) {
      setError(t('account.errors.unable_to_create'));
      return;
    }

    setIsLoading(true);
    try {
      // Step 0: refuse a name another merchant of the same partner already
      // uses. This runs BEFORE createOrganization on purpose — the Clerk org
      // is created client-side, so once it exists the duplicate is on the
      // books and only an operator can undo it. Unaffiliated merchants are
      // never blocked (the server answers `available` when there is no
      // partner). Advisory, not enforced: the blocking guard lives in the
      // partner provisioning services.
      // Fails OPEN: this is a convenience check, so a network blip or a
      // cold start must never stand between a merchant and their account.
      // The blocking guard lives server-side in the partner provisioning
      // services and in completeHubSignup.
      let nameIsTaken = false;
      try {
        const availability = await utils.account.checkNameAvailability.fetch({
          name: accountName,
          partner: resolveSignupPartner(currentPartnerBrand()),
        });
        nameIsTaken = !availability.available;
      } catch (checkError) {
        console.warn('Name availability check failed, continuing:', checkError);
      }
      if (nameIsTaken) {
        setError(t('account.errors.name_taken_for_partner'));
        setIsLoading(false);
        return;
      }

      // Step 1: Create the organization
      const org = await createOrganization({
        name: accountName,
      });

      if (!org?.id || !org?.slug) {
        throw new Error('Failed to create organization');
      }

      // Ensure slug is not null (TypeScript guard)
      const orgSlug: string = org.slug;

      // Step 2: Update organization acquisition metadata via backend
      try {
        const acquisitionMetadata = getAcquisitionMetadataForOrganization(user);
        const acquisitionData = acquisitionMetadata.acquisition;

        if (acquisitionData) {
          await updateOrgAcquisitionMutation.mutateAsync({
            orgId: org.id,
            acquisitionData,
          });
          console.log('✅ Organization acquisition metadata updated');
        }
      } catch (metadataError) {
        console.error('Failed to update organization acquisition metadata:', metadataError);
        // Don't fail the whole process if metadata update fails
      }

      // Step 3: Set the active organization
      if (setActive) {
        await setActive({
          organization: org.id,
        });
      }

      // Step 4: Provision the Tagada DB account (must happen before any protectedProcedure calls).
      // Resolve partner attribution: host-based (Partner Mode) wins
      // over URL-captured (?partner=). Server validates against the
      // `partners` DB table and rejects unknown slugs silently.
      const resolvedPartner = resolveSignupPartner(currentPartnerBrand());
      try {
        await provisionAccountMutation.mutateAsync({
          partner: resolvedPartner,
        });
        // Attribution applied (or harmlessly ignored). Clear the
        // captured value so a subsequent account creation in the
        // same browser doesn't carry over a stale attribution.
        clearCapturedPartner();
      } catch (provisionError) {
        console.error('Failed to provision account:', provisionError);
        throw new Error('Failed to set up your account. Please try again.');
      }

      // Step 5: Update billing email
      try {
        await updateBillingEmailMutation.mutateAsync({
          email: billingEmail,
        });
      } catch (emailError) {
        console.error('Failed to set billing email:', emailError);
        toast.error('Warning: Could not set billing email. You can update it later.');
      }

      // Step 6: Redirect to Stripe payment setup
      try {
        // Check if there's an active onboarding intent
        const onboardingState = getOnboardingState();

        // Construct return URL based on onboarding state
        let returnUrl: string;

        if (onboardingState?.intent) {
          // User is onboarding - go directly to store creation to continue flow
          returnUrl = `${window.location.origin}/org/${orgSlug}/stores/create`;
          console.log('Creating payment session for onboarding with return URL:', returnUrl);
        } else {
          // Normal account creation - go to billing page
          returnUrl = `${window.location.origin}/org/${orgSlug}/billing?tab=payment-methods`;
          console.log('Creating payment session for new account with return URL:', returnUrl);
        }

        const result = await createPaymentSessionMutation.mutateAsync({ returnUrl });
        if (result?.url) {
          // Redirect to the provider's hosted card page (Stripe Customer Portal)
          window.location.href = result.url;
          return;
        }
        // No hosted page on this provider (Checkout.com): the activation
        // gate collects the card in the CRM on the next navigation.
      } catch (paymentError) {
        console.error('Failed to create payment session:', paymentError);
        // If payment setup fails, still let user into their account
        toast.error('Warning: Could not initiate payment setup. You can add it later from billing settings.');
        onAccountCreated(orgSlug);
        return;
      }

      // Fallback: if no redirect URL, just complete
      onAccountCreated(orgSlug);
    } catch (err: any) {
      console.error('Error creating account:', err);
      setError(getErrorMessage(err));
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && canCreate && !isLoading) {
      handleCreateAccount();
    }
  };

  const canCreate = accountName.trim().length >= 3 && billingEmail && !emailError && !error;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="bg-primary/10 flex h-12 w-12 items-center justify-center rounded-lg">
          <Building2 className="text-primary h-6 w-6" />
        </div>
        <div>
          <h2 className="text-foreground text-lg font-semibold">{t('account.create.title')}</h2>
          <p className="text-muted-foreground text-sm">
            Create your organization to manage multiple stores and projects
          </p>
        </div>
      </div>

      {/* Account Name */}
      <div className="space-y-2">
        <Label htmlFor="account-name" className="text-foreground font-medium">
          {t('account.create.accountName')} <span className="text-red-500">*</span>
        </Label>
        <Input
          id="account-name"
          name="account-name"
          placeholder={t('account.create.accountNamePlaceholder')}
          value={accountName}
          onChange={handleAccountNameChange}
          onKeyDown={handleKeyDown}
          disabled={isLoading}
          autoFocus
        />
        {error && <p className="text-sm text-red-500 dark:text-red-400">{error}</p>}
        <p className="text-muted-foreground flex items-start gap-2 text-sm">
          <Info className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>Your account name (e.g., "Acme Corp"). You can add multiple stores inside this account.</span>
        </p>
      </div>

      {/* Billing Email */}
      <div className="space-y-2">
        <Label htmlFor="billing-email" className="text-foreground font-medium">
          {t('account.create.billingEmail')} <span className="text-red-500">*</span>
        </Label>
        <div className="relative">
          <Mail className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
          <Input
            id="billing-email"
            name="billing-email"
            type="email"
            placeholder={t('account.create.billingEmailPlaceholder')}
            value={billingEmail}
            onChange={handleBillingEmailChange}
            onKeyDown={handleKeyDown}
            disabled={isLoading}
            className="pl-9"
          />
        </div>
        {emailError && <p className="text-sm text-red-500 dark:text-red-400">{emailError}</p>}
        <p className="text-muted-foreground flex items-start gap-2 text-sm">
          <Info className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>{t('account.create.billingEmailHelper')}</span>
        </p>
      </div>

      {/* Payment Method Info */}
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-900 dark:bg-blue-950/20">
        <div className="flex items-start gap-3">
          <CreditCard className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600 dark:text-blue-400" />
          <div className="flex-1">
            <h4 className="mb-1 text-sm font-semibold text-blue-900 dark:text-blue-100">
              {t('account.create.paymentMethod')}
            </h4>
            <p className="text-sm text-blue-700 dark:text-blue-300">
              {t('account.create.paymentMethodHelper')} You'll be redirected to securely add your payment
              information after creating your account.
            </p>
            <p className="mt-2 text-xs font-medium text-blue-600 dark:text-blue-400">
              💳 Your card won't be charged until you run live transactions
            </p>
          </div>
        </div>
      </div>

      {/* Benefits */}
      <div className="bg-primary/5 rounded-lg p-4">
        <h4 className="text-foreground mb-2 text-sm font-semibold">What you'll get:</h4>
        <ul className="text-muted-foreground space-y-1.5 text-sm">
          <li className="flex items-start gap-2">
            <span className="text-primary mt-1">•</span>
            <span>
              <strong>Multiple stores</strong> under one account
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-primary mt-1">•</span>
            <span>Unlimited test transactions (no charges)</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-primary mt-1">•</span>
            <span>Access to all plugins and features</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-primary mt-1">•</span>
            <span>Team collaboration tools</span>
          </li>
        </ul>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-2">
        {onCancel && (
          <Button variant="outline" onClick={onCancel} disabled={isLoading}>
            {t('account.create.cancel')}
          </Button>
        )}
        <Button id="create-org-button" onClick={handleCreateAccount} disabled={!canCreate || isLoading}>
          {isLoading ? (
            <div className="flex items-center gap-2">
              <div className="border-primary-foreground h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" />
              {t('account.create.creating')}
            </div>
          ) : (
            t('account.create.createButton')
          )}
        </Button>
      </div>
    </div>
  );
};
